import { del, get, post, put } from '@api/api';
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient
} from '@tanstack/react-query';
import { AnalyticsActionName } from '@utils/analytics';
import API_ROUTES from 'api/routes';
import useAnalytics from 'hooks/useAnalytics';
import {
	AccessKeyStatus,
	IAccessKey,
	IGenerateAccessKey,
	IGeneratedAccessKey
} from 'types/accessKeys';
import { IDataResponse } from 'types/common';
import QueryKeys from '.';

const onKeyAccessUpdated =
	(queryClient: QueryClient) => (res: IDataResponse<IAccessKey>) => {
		const updatedKey = res.data ? res.data[0] : null;
		const allAccessKeys = queryClient.getQueryData<IDataResponse<IAccessKey>>([
			QueryKeys.managementKeys
		]);
		if (updatedKey && allAccessKeys) {
			queryClient.setQueryData([QueryKeys.managementKeys], {
				...allAccessKeys,
				data: (allAccessKeys.data || []).map((key) =>
					key.id === updatedKey.id ? updatedKey : key
				)
			});
		}
	};

const getManagementKeys = async () => {
	const res = await get<IDataResponse<IAccessKey>>(
		API_ROUTES.v1.managementKeys.all
	);
	return res.data;
};

const editManagementKey = async (key: IAccessKey) => {
	const res = await put<IAccessKey, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.managementKeys.update,
		key
	);
	return res.data;
};

const deleteManagementKeyDel = async (key: string) => {
	await del(API_ROUTES.v1.managementKeys.delete(key));
	return key;
};

const deactivate = async (key: string) => {
	const res = await post<{ ids: string[] }, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.managementKeys.deactivate,
		{
			ids: [key]
		}
	);
	return res.data;
};

const activateKey = async (key: string) => {
	const res = await post<{ ids: string[] }, IDataResponse<IAccessKey>>(
		API_ROUTES.v1.managementKeys.activate,
		{
			ids: [key]
		}
	);
	return res.data;
};

const generateManagementKeyPost = async (key: IGenerateAccessKey) => {
	if (key.allProjects) {
		delete key.permittedProjects;
	}
	delete key.allProjects;
	const res = await post<IGenerateAccessKey, IGeneratedAccessKey>(
		API_ROUTES.v1.managementKeys.generate,
		key
	);
	return res.data;
};

export const useGenerateManagementKey = () => {
	const queryClient = useQueryClient();
	const track = useAnalytics();
	return useMutation<IGeneratedAccessKey, unknown, IGenerateAccessKey>(
		async (value) => generateManagementKeyPost(value),
		{
			onSuccess: ({ key }) => {
				const allKeys = queryClient.getQueryData<IDataResponse<IAccessKey>>([
					QueryKeys.managementKeys
				]);
				if (allKeys) {
					queryClient.setQueryData([QueryKeys.managementKeys], {
						...allKeys,
						data: [...(allKeys.data || []), key],
						total: allKeys.total + 1
					});
				}

				track(AnalyticsActionName.managementKeyCreation);
			}
		}
	);
};

export const useUpdateManagementKeyStatus = () => {
	const queryClient = useQueryClient();
	return useMutation<
		IDataResponse<IAccessKey>,
		unknown,
		{ key: string; status: AccessKeyStatus }
	>(
		async ({ key, status }) =>
			status === AccessKeyStatus.active ? deactivate(key) : activateKey(key),
		{
			onSuccess: onKeyAccessUpdated(queryClient)
		}
	);
};

export const useUpdateManagementKey = () => {
	const queryClient = useQueryClient();
	return useMutation<IDataResponse<IAccessKey>, unknown, IAccessKey>(
		async (key: IAccessKey) => editManagementKey(key),
		{
			onSuccess: onKeyAccessUpdated(queryClient)
		}
	);
};

export const useDeleteManagementKey = () => {
	const queryClient = useQueryClient();
	return useMutation<string, unknown, string>(
		async (key: string) => deleteManagementKeyDel(key),
		{
			onSuccess: (deleteKeyId: string) => {
				const allKeys = queryClient.getQueryData<IDataResponse<IAccessKey>>([
					QueryKeys.managementKeys
				]);
				if (allKeys) {
					queryClient.setQueryData([QueryKeys.managementKeys], {
						...allKeys,
						data: (allKeys.data || []).filter(
							(item) => item.id !== deleteKeyId
						),
						total: allKeys.total - 1
					});
				}
			}
		}
	);
};

export const useManagementKeys = () =>
	useQuery([QueryKeys.managementKeys], getManagementKeys, {
		staleTime: 100000
	});
